<div class="w-full mx-auto min-h-screen p-5 bg-gradient-to-br from-gray-50 to-blue-50 dark:from-gray-900 dark:to-blue-900/20">
  
  <!-- Loading State -->
  <div *ngIf="loading" class="flex items-center justify-center py-20">
    <div class="animate-spin rounded-full h-16 w-16 border-b-2 border-green-600"></div>
  </div>

  <div *ngIf="!loading && request" class="space-y-6 animate-fade-in">
    <!-- Header with Back Button -->
    <div class="flex items-center gap-4 mb-6">
      <button (click)="goBack()"
              class="flex items-center justify-center h-10 w-10 rounded-lg bg-white border border-gray-300 hover:bg-gray-50 transition-colors dark:bg-boxdark dark:border-strokedark dark:hover:bg-gray-800">
        <svg class="h-5 w-5 text-gray-600 dark:text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"/>
        </svg>
      </button>
      <div class="flex-1">
        <h1 class="text-3xl font-bold text-gray-900 dark:text-white">Detalle de Webhook Wompi</h1>
        <p class="text-gray-600 dark:text-gray-300 mt-1">Transacción ID: <span class="font-mono font-semibold">{{ request.transactionId }}</span></p>
      </div>
    </div>

    <!-- Status Card -->
    <div class="rounded-2xl border border-stroke bg-gradient-to-br from-white to-gray-50 p-8 shadow-xl dark:border-strokedark dark:from-boxdark dark:to-gray-900/50">
      <div class="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
        <div class="flex items-center">          
          <div>
            <p class="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">Estado del Webhook</p>
            <span class="inline-flex items-center px-4 py-2 rounded-full text-lg font-semibold border-2"
                  [ngClass]="{
                    'bg-cyan-50 text-cyan-700 border-cyan-200 dark:bg-cyan-900/20 dark:text-cyan-400 dark:border-cyan-800': request.status === WompiRequestStatus.Received,
                    'bg-yellow-50 text-yellow-700 border-yellow-200 dark:bg-yellow-900/20 dark:text-yellow-400 dark:border-yellow-800': request.status === WompiRequestStatus.Processing,
                    'bg-green-50 text-green-700 border-green-200 dark:bg-green-900/20 dark:text-green-400 dark:border-green-800': request.status === WompiRequestStatus.Success,
                    'bg-red-50 text-red-700 border-red-200 dark:bg-red-900/20 dark:text-red-400 dark:border-red-800': request.status === WompiRequestStatus.Failed
                  }">
              <svg class="w-3 h-3 mr-2" fill="currentColor" viewBox="0 0 8 8">
                <circle cx="4" cy="4" r="3"/>
              </svg>
              {{ getStatusName(request.status) }}
            </span>
          </div>
        </div>

        <div class="flex flex-col sm:flex-row gap-3">
          <div class="rounded-lg bg-gradient-to-br from-green-50 to-white p-4 border border-green-100 dark:from-green-900/10 dark:to-boxdark dark:border-green-800/30">
            <p class="text-sm font-medium text-gray-600 dark:text-gray-400">Reintentos</p>
            <p class="text-3xl font-bold mt-1"
               [ngClass]="request.retryCount > 0 ? 'text-orange-600 dark:text-orange-400' : 'text-gray-400'">
              {{ request.retryCount }}
            </p>
            <p *ngIf="request.lastRetryDate" class="text-xs text-gray-500 dark:text-gray-400 mt-1">
              Último: {{ formatDate(request.lastRetryDate) }}
            </p>
          </div>

          <button *ngIf="request.status === WompiRequestStatus.Failed || request.status === WompiRequestStatus.Received"
                  (click)="retryRequest()"
                  class="group relative inline-flex items-center justify-center px-8 py-4 overflow-hidden font-medium text-white bg-gradient-to-br from-blue-500 to-blue-700 rounded-lg shadow-lg transition-all duration-300 ease-out hover:scale-105 hover:shadow-xl hover:from-blue-600 hover:to-blue-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2">
            <svg class="relative mr-2 h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/>
            </svg>
            <span class="relative">Reprocesar Webhook</span>
            <div class="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent opacity-0 group-hover:opacity-100 transform -translate-x-full group-hover:translate-x-full transition-all duration-700"></div>
          </button>
        </div>
      </div>
    </div>

    <!-- Error Section (if failed) -->
    <div *ngIf="request.status === WompiRequestStatus.Failed && request.errorMessage"
         class="rounded-xl border-2 border-red-300 bg-gradient-to-br from-red-50 to-white p-6 shadow-lg dark:border-red-800 dark:from-red-900/20 dark:to-boxdark">
      <div class="flex items-start gap-4 mb-4">
        <div class="flex h-10 w-10 items-center justify-center rounded-full bg-red-100 dark:bg-red-900/30">
          <svg class="h-6 w-6 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-1.964-1.333-2.732 0L3.732 16c-.77 1.333.192 3 1.732 3z"/>
          </svg>
        </div>
        <div class="flex-1">
          <h3 class="text-lg font-semibold text-red-900 dark:text-red-400 mb-2">Error en el Procesamiento</h3>
          
          <div class="mb-4">
            <p class="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Paso donde falló:</p>
            <span class="inline-flex items-center px-3 py-1 rounded-lg bg-red-100 text-red-800 text-sm font-medium dark:bg-red-900/30 dark:text-red-400">
              {{ request.failureStep || 'No especificado' }}
            </span>
          </div>

          <div class="mb-4">
            <p class="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Mensaje de error:</p>
            <div class="p-4 bg-white rounded-lg border border-red-200 dark:bg-boxdark dark:border-red-800">
              <p class="text-sm text-red-700 dark:text-red-400">{{ request.errorMessage }}</p>
            </div>
          </div>

          <div *ngIf="request.errorDetails" class="mb-4">
            <div class="flex items-center justify-between mb-2">
              <p class="text-sm font-medium text-gray-700 dark:text-gray-300">Detalles técnicos:</p>
              <button (click)="copyToClipboard(request.errorDetails)"
                      class="inline-flex items-center px-3 py-1 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors dark:bg-boxdark dark:border-strokedark dark:text-gray-300 dark:hover:bg-gray-800">
                <svg class="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"/>
                </svg>
                Copiar
              </button>
            </div>
            <pre class="p-4 bg-gray-900 text-gray-100 rounded-lg text-xs overflow-x-auto max-h-64 border border-gray-700">{{ request.errorDetails }}</pre>
          </div>

          <div class="p-4 bg-yellow-50 border border-yellow-200 rounded-lg dark:bg-yellow-900/10 dark:border-yellow-800/30">
            <div class="flex items-start">
              <svg class="h-5 w-5 text-yellow-600 dark:text-yellow-400 mt-0.5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
              </svg>
              <div>
                <p class="text-sm font-medium text-yellow-800 dark:text-yellow-400">Recomendación</p>
                <p class="text-sm text-yellow-700 dark:text-yellow-500 mt-1">
                  Revise los datos del webhook, corrija el problema identificado (especialmente en <strong>{{ request.failureStep }}</strong>) y use el botón "Reprocesar Webhook" para intentar nuevamente.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Processing Logs Section -->
    <div *ngIf="processingLogs && processingLogs.length > 0" 
         class="rounded-xl border border-stroke bg-white shadow-default dark:border-strokedark dark:bg-boxdark">
      <div class="border-b border-stroke px-6 py-4 dark:border-strokedark">
        <div class="flex items-center justify-between">
          <h3 class="font-semibold text-gray-900 dark:text-white flex items-center">
            <svg class="mr-2 h-5 w-5 text-indigo-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01"/>
            </svg>
            Logs de Procesamiento
            <span class="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-400">
              {{ processingLogs.length }} entradas
            </span>
          </h3>
          <button (click)="toggleProcessingLogs()"
                  class="inline-flex items-center px-3 py-1.5 text-sm font-medium text-indigo-700 bg-indigo-100 rounded-lg hover:bg-indigo-200 transition-colors dark:bg-indigo-900/30 dark:text-indigo-400 dark:hover:bg-indigo-900/50">
            <svg class="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" [attr.d]="showProcessingLogs ? 'M19 9l-7 7-7-7' : 'M9 5l7 7-7 7'"/>
            </svg>
            {{ showProcessingLogs ? 'Ocultar' : 'Mostrar' }}
          </button>
        </div>
      </div>
      <div *ngIf="showProcessingLogs" class="p-6">
        <div class="space-y-2">
          <div *ngFor="let log of processingLogs; let i = index" 
               class="flex items-start gap-3 p-3 rounded-lg border transition-colors"
               [ngClass]="{
                 'bg-blue-50 border-blue-200 dark:bg-blue-900/10 dark:border-blue-800/30': log.Level === 'INF',
                 'bg-yellow-50 border-yellow-200 dark:bg-yellow-900/10 dark:border-yellow-800/30': log.Level === 'WRN',
                 'bg-red-50 border-red-200 dark:bg-red-900/10 dark:border-red-800/30': log.Level === 'ERR'
               }">
            <!-- Log Level Icon -->
            <div class="flex-shrink-0 mt-0.5">
              <svg class="w-5 h-5" 
                   [ngClass]="{
                     'text-blue-500': log.Level === 'INF',
                     'text-yellow-500': log.Level === 'WRN',
                     'text-red-500': log.Level === 'ERR'
                   }"
                   fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" [attr.d]="getLogLevelIcon(log.Level)"/>
              </svg>
            </div>
            
            <!-- Log Content -->
            <div class="flex-1 min-w-0">
              <div class="flex items-center gap-2 mb-1">
                <span class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium"
                      [ngClass]="getLogLevelClass(log.Level)">
                  {{ log.Level }}
                </span>
                <span *ngIf="log.Code" class="inline-flex items-center px-2 py-0.5 rounded text-xs font-mono bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300">
                  {{ log.Code }}
                </span>
                <span class="text-xs text-gray-500 dark:text-gray-400">
                  {{ formatLogTimestamp(log.Timestamp) }}
                </span>
              </div>
              <p class="text-sm text-gray-900 dark:text-white break-words">{{ log.Message }}</p>
            </div>
            
            <!-- Step Number -->
            <div class="flex-shrink-0">
              <span class="inline-flex items-center justify-center w-6 h-6 rounded-full bg-gray-200 text-gray-600 text-xs font-medium dark:bg-gray-700 dark:text-gray-300">
                {{ i + 1 }}
              </span>
            </div>
          </div>
        </div>
        
        <!-- Summary -->
        <div class="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
          <div class="flex items-center gap-4 text-sm">
            <span class="text-gray-500 dark:text-gray-400">Resumen:</span>
            <span class="inline-flex items-center gap-1">
              <span class="w-2 h-2 rounded-full bg-blue-500"></span>
              <span class="text-gray-600 dark:text-gray-300">{{ getLogCountByLevel('INF') }} Info</span>
            </span>
            <span class="inline-flex items-center gap-1">
              <span class="w-2 h-2 rounded-full bg-yellow-500"></span>
              <span class="text-gray-600 dark:text-gray-300">{{ getLogCountByLevel('WRN') }} Warnings</span>
            </span>
            <span class="inline-flex items-center gap-1">
              <span class="w-2 h-2 rounded-full bg-red-500"></span>
              <span class="text-gray-600 dark:text-gray-300">{{ getLogCountByLevel('ERR') }} Errors</span>
            </span>
          </div>
        </div>
      </div>
    </div>

    <!-- Info Grid -->
    <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <!-- Transaction Info -->
      <div class="rounded-xl border border-stroke bg-white shadow-default dark:border-strokedark dark:bg-boxdark">
        <div class="border-b border-stroke px-6 py-4 dark:border-strokedark">
          <h3 class="font-semibold text-gray-900 dark:text-white flex items-center">
            <svg class="mr-2 h-5 w-5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"/>
            </svg>
            Información de la Transacción
          </h3>
        </div>
        <div class="p-6 space-y-4">
          <div class="flex items-start">
            <span class="text-sm font-medium text-gray-500 dark:text-gray-400 w-40">ID Transacción:</span>
            <span class="text-sm font-semibold text-gray-900 dark:text-white font-mono">{{ request.transactionId }}</span>
          </div>
          <div class="flex items-start">
            <span class="text-sm font-medium text-gray-500 dark:text-gray-400 w-40">Referencia:</span>
            <span class="text-sm text-gray-900 dark:text-white">{{ request.reference || '-' }}</span>
          </div>
          <div class="flex items-start">
            <span class="text-sm font-medium text-gray-500 dark:text-gray-400 w-40">Evento:</span>
            <span class="text-sm text-gray-900 dark:text-white">{{ request.event || '-' }}</span>
          </div>
          <div class="flex items-start">
            <span class="text-sm font-medium text-gray-500 dark:text-gray-400 w-40">Estado Transacción:</span>
            <span *ngIf="request.transactionStatus" class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
                  [ngClass]="getTransactionStatusColor(request.transactionStatus)">
              {{ request.transactionStatus }}
            </span>
            <span *ngIf="!request.transactionStatus" class="text-sm text-gray-400">-</span>
          </div>
          <div class="flex items-start">
            <span class="text-sm font-medium text-gray-500 dark:text-gray-400 w-40">Monto:</span>
            <span class="text-sm font-semibold text-gray-900 dark:text-white">{{ formatAmount(request.amountInCents, request.currency || undefined) }}</span>
          </div>
          <div class="flex items-start">
            <span class="text-sm font-medium text-gray-500 dark:text-gray-400 w-40">Método de Pago:</span>
            <div class="flex items-center gap-2">
              <svg class="h-4 w-4 text-gray-400" [class]="getPaymentMethodIcon(request.paymentMethodType || '')" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"/>
              </svg>
              <span class="text-sm text-gray-900 dark:text-white">{{ request.paymentMethodType || '-' }}</span>
            </div>
          </div>
          <div class="flex items-start">
            <span class="text-sm font-medium text-gray-500 dark:text-gray-400 w-40">Payment Link ID:</span>
            <span class="text-sm text-gray-900 dark:text-white">{{ request.paymentLinkId || '-' }}</span>
          </div>
          <div *ngIf="request.processingTimeMs" class="flex items-start">
            <span class="text-sm font-medium text-gray-500 dark:text-gray-400 w-40">Tiempo Procesamiento:</span>
            <span class="text-sm text-gray-900 dark:text-white">{{ formatProcessingTime(request.processingTimeMs) }}</span>
          </div>
          <div *ngIf="request.completedStep" class="flex items-start">
            <span class="text-sm font-medium text-gray-500 dark:text-gray-400 w-40">Paso Completado:</span>
            <span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400">
              {{ request.completedStep }}
            </span>
          </div>
        </div>
      </div>

      <!-- Customer Info -->
      <div class="rounded-xl border border-stroke bg-white shadow-default dark:border-strokedark dark:bg-boxdark">
        <div class="border-b border-stroke px-6 py-4 dark:border-strokedark">
          <h3 class="font-semibold text-gray-900 dark:text-white flex items-center">
            <svg class="mr-2 h-5 w-5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"/>
            </svg>
            Información del Cliente
          </h3>
        </div>
        <div class="p-6 space-y-4">
          <div class="flex items-start">
            <span class="text-sm font-medium text-gray-500 dark:text-gray-400 w-40">Nombre:</span>
            <span class="text-sm text-gray-900 dark:text-white">{{ request.customerName || '-' }}</span>
          </div>
          <div class="flex items-start">
            <span class="text-sm font-medium text-gray-500 dark:text-gray-400 w-40">Email:</span>
            <span class="text-sm text-gray-900 dark:text-white">{{ request.customerEmail || '-' }}</span>
          </div>
          <div class="flex items-start">
            <span class="text-sm font-medium text-gray-500 dark:text-gray-400 w-40">Documento:</span>
            <span class="text-sm text-gray-900 dark:text-white">{{ request.customerLegalId || '-' }}</span>
          </div>
          <div class="flex items-start">
            <span class="text-sm font-medium text-gray-500 dark:text-gray-400 w-40">Tipo Documento:</span>
            <span class="text-sm text-gray-900 dark:text-white">{{ request.customerLegalIdType || '-' }}</span>
          </div>
          <div class="flex items-start">
            <span class="text-sm font-medium text-gray-500 dark:text-gray-400 w-40">Participant ID:</span>
            <div class="flex items-center gap-2">
              <span *ngIf="request.participantId" class="text-sm text-gray-900 dark:text-white">{{ request.participantId }}</span>
              <button *ngIf="request.participantId"
                      (click)="viewParticipant()"
                      class="inline-flex items-center px-2 py-1 text-xs font-medium text-blue-700 bg-blue-100 rounded hover:bg-blue-200 transition-colors dark:bg-blue-900/30 dark:text-blue-400 dark:hover:bg-blue-900/50">
                <svg class="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/>
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/>
                </svg>
                Ver
              </button>
              <span *ngIf="!request.participantId" class="text-sm text-gray-400">-</span>
            </div>
          </div>
          <div class="flex items-start">
            <span class="text-sm font-medium text-gray-500 dark:text-gray-400 w-40">Enrollment ID:</span>
            <div class="flex items-center gap-2">
              <span *ngIf="request.enrollmentId" class="text-sm text-gray-900 dark:text-white">{{ request.enrollmentId }}</span>
              <button *ngIf="request.enrollmentId"
                      (click)="viewEnrollment()"
                      class="inline-flex items-center px-2 py-1 text-xs font-medium text-blue-700 bg-blue-100 rounded hover:bg-blue-200 transition-colors dark:bg-blue-900/30 dark:text-blue-400 dark:hover:bg-blue-900/50">
                <svg class="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/>
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/>
                </svg>
                Ver
              </button>
              <span *ngIf="!request.enrollmentId" class="text-sm text-gray-400">-</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Dates and Environment Info -->
    <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <!-- Dates -->
      <div class="rounded-xl border border-stroke bg-white shadow-default dark:border-strokedark dark:bg-boxdark">
        <div class="border-b border-stroke px-6 py-4 dark:border-strokedark">
          <h3 class="font-semibold text-gray-900 dark:text-white flex items-center">
            <svg class="mr-2 h-5 w-5 text-purple-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
            </svg>
            Fechas
          </h3>
        </div>
        <div class="p-6 space-y-4">
          <div class="flex items-start">
            <span class="text-sm font-medium text-gray-500 dark:text-gray-400 w-40">Recibido:</span>
            <span class="text-sm text-gray-900 dark:text-white">{{ formatDate(request.requestDate) }}</span>
          </div>
          <div class="flex items-start">
            <span class="text-sm font-medium text-gray-500 dark:text-gray-400 w-40">Procesado:</span>
            <span class="text-sm text-gray-900 dark:text-white">{{ formatDate(request.processedDate) }}</span>
          </div>
          <div *ngIf="request.transactionFinalizedAt" class="flex items-start">
            <span class="text-sm font-medium text-gray-500 dark:text-gray-400 w-40">Finalizado en Wompi:</span>
            <span class="text-sm text-gray-900 dark:text-white">{{ formatDate(request.transactionFinalizedAt) }}</span>
          </div>
          <div *ngIf="request.wompiTimestamp" class="flex items-start">
            <span class="text-sm font-medium text-gray-500 dark:text-gray-400 w-40">Timestamp Wompi:</span>
            <span class="text-sm text-gray-900 dark:text-white font-mono">{{ request.wompiTimestamp }}</span>
          </div>
        </div>
      </div>

      <!-- Environment -->
      <div class="rounded-xl border border-stroke bg-white shadow-default dark:border-strokedark dark:bg-boxdark">
        <div class="border-b border-stroke px-6 py-4 dark:border-strokedark">
          <h3 class="font-semibold text-gray-900 dark:text-white flex items-center">
            <svg class="mr-2 h-5 w-5 text-indigo-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9v-9m0-9v9"/>
            </svg>
            Ambiente y Sistema
          </h3>
        </div>
        <div class="p-6 space-y-4">
          <div class="flex items-start">
            <span class="text-sm font-medium text-gray-500 dark:text-gray-400 w-40">Ambiente:</span>
            <span *ngIf="request.environment" class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
                  [ngClass]="request.environment === 'production' ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400' : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400'">
              {{ request.environment }}
            </span>
            <span *ngIf="!request.environment" class="text-sm text-gray-400">-</span>
          </div>
          <div class="flex items-start">
            <span class="text-sm font-medium text-gray-500 dark:text-gray-400 w-40">Payment Link Interno:</span>
            <div class="flex items-center gap-2">
              <span *ngIf="request.paymentLinkId_Internal" class="text-sm text-gray-900 dark:text-white">{{ request.paymentLinkId_Internal }}</span>
              <button *ngIf="request.paymentLinkId_Internal"
                      (click)="viewPaymentLink()"
                      class="inline-flex items-center px-2 py-1 text-xs font-medium text-green-700 bg-green-100 rounded hover:bg-green-200 transition-colors dark:bg-green-900/30 dark:text-green-400 dark:hover:bg-green-900/50">
                <svg class="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/>
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/>
                </svg>
                Ver
              </button>
              <span *ngIf="!request.paymentLinkId_Internal" class="text-sm text-gray-400">-</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- HTTP Request Info -->
    <div *ngIf="request.httpRequestInfo" class="rounded-xl border border-stroke bg-white shadow-default dark:border-strokedark dark:bg-boxdark">
      <div class="border-b border-stroke px-6 py-4 dark:border-strokedark">
        <div class="flex items-center justify-between">
          <h3 class="font-semibold text-gray-900 dark:text-white flex items-center">
            <svg class="mr-2 h-5 w-5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9v-9m0-9v9"/>
            </svg>
            Información de la Petición HTTP
          </h3>
          <div class="flex gap-2">
            <button (click)="copyToClipboard(httpRequestInfoFormatted)"
                    class="inline-flex items-center px-3 py-1.5 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600">
              <svg class="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"/>
              </svg>
              Copiar
            </button>
            <button (click)="toggleHttpRequestInfo()"
                    class="inline-flex items-center px-3 py-1.5 text-sm font-medium text-blue-700 bg-blue-100 rounded-lg hover:bg-blue-200 transition-colors dark:bg-blue-900/30 dark:text-blue-400 dark:hover:bg-blue-900/50">
              <svg class="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" [attr.d]="showHttpRequestInfo ? 'M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21' : 'M15 12a3 3 0 11-6 0 3 3 0 016 0z M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z'"/>
              </svg>
              {{ showHttpRequestInfo ? 'Ocultar' : 'Mostrar' }}
            </button>
          </div>
        </div>
      </div>
      <div *ngIf="showHttpRequestInfo" class="p-6">
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div *ngIf="httpRequestData?.RemoteIpAddress" class="flex items-start">
            <span class="text-sm font-medium text-gray-500 dark:text-gray-400 w-24">IP:</span>
            <span class="text-sm text-gray-900 dark:text-white font-mono">{{ httpRequestData.RemoteIpAddress }}</span>
          </div>
          <div *ngIf="httpRequestData?.UserAgent" class="flex items-start">
            <span class="text-sm font-medium text-gray-500 dark:text-gray-400 w-24">User-Agent:</span>
            <span class="text-sm text-gray-900 dark:text-white break-all">{{ httpRequestData.UserAgent }}</span>
          </div>
          <div *ngIf="httpRequestData?.Method" class="flex items-start">
            <span class="text-sm font-medium text-gray-500 dark:text-gray-400 w-24">Método:</span>
            <span class="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400">
              {{ httpRequestData.Method }}
            </span>
          </div>
          <div *ngIf="httpRequestData?.Timestamp" class="flex items-start">
            <span class="text-sm font-medium text-gray-500 dark:text-gray-400 w-24">Timestamp:</span>
            <span class="text-sm text-gray-900 dark:text-white">{{ formatDate(httpRequestData.Timestamp) }}</span>
          </div>
          <div *ngIf="httpRequestData?.ContentType" class="flex items-start">
            <span class="text-sm font-medium text-gray-500 dark:text-gray-400 w-24">Content-Type:</span>
            <span class="text-sm text-gray-900 dark:text-white">{{ httpRequestData.ContentType }}</span>
          </div>
          <div *ngIf="httpRequestData?.ContentLength" class="flex items-start">
            <span class="text-sm font-medium text-gray-500 dark:text-gray-400 w-24">Content-Length:</span>
            <span class="text-sm text-gray-900 dark:text-white">{{ httpRequestData.ContentLength }} bytes</span>
          </div>
        </div>
        <div class="mb-4">
          <p class="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Headers HTTP:</p>
          <div class="p-4 bg-gray-50 rounded-lg border dark:bg-gray-800 dark:border-gray-700 max-h-48 overflow-y-auto">
            <div *ngFor="let header of getHttpHeaders()" class="flex items-start py-1 border-b border-gray-200 dark:border-gray-700 last:border-b-0">
              <span class="text-xs font-medium text-gray-600 dark:text-gray-400 w-32 flex-shrink-0">{{ header.key }}:</span>
              <span class="text-xs text-gray-900 dark:text-white break-all ml-2">{{ header.value }}</span>
            </div>
          </div>
        </div>
        <div *ngIf="httpRequestData?.Body" class="mb-4">
          <div class="flex items-center justify-between mb-2">
            <p class="text-sm font-medium text-gray-700 dark:text-gray-300">Body del Request:</p>
            <button (click)="copyToClipboard(httpRequestData.Body)"
                    class="inline-flex items-center px-3 py-1 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors dark:bg-boxdark dark:border-strokedark dark:text-gray-300 dark:hover:bg-gray-800">
              <svg class="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"/>
              </svg>
              Copiar
            </button>
          </div>
          <pre class="p-4 bg-gray-900 text-gray-100 rounded-lg text-xs overflow-x-auto max-h-64 border border-gray-700">{{ httpRequestData.Body }}</pre>
        </div>
        <div>
          <p class="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Datos completos (JSON):</p>
          <pre class="p-4 bg-gray-900 text-gray-100 rounded-lg text-xs overflow-x-auto max-h-64 border border-gray-700">{{ httpRequestInfoFormatted }}</pre>
        </div>
      </div>
    </div>

    <!-- Raw Webhook Data -->
    <div *ngIf="request.rawWebhookData" class="rounded-xl border border-stroke bg-white shadow-default dark:border-strokedark dark:bg-boxdark">
      <div class="border-b border-stroke px-6 py-4 dark:border-strokedark">
        <div class="flex items-center justify-between">
          <h3 class="font-semibold text-gray-900 dark:text-white flex items-center">
            <svg class="mr-2 h-5 w-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4"/>
            </svg>
            Datos del Webhook (JSON)
          </h3>
          <div class="flex gap-2">
            <button (click)="copyToClipboard(rawWebhookDataFormatted)"
                    class="inline-flex items-center px-3 py-1.5 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600">
              <svg class="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"/>
              </svg>
              Copiar
            </button>
            <button (click)="toggleRawData()"
                    class="inline-flex items-center px-3 py-1.5 text-sm font-medium text-green-700 bg-green-100 rounded-lg hover:bg-green-200 transition-colors dark:bg-green-900/30 dark:text-green-400 dark:hover:bg-green-900/50">
              <svg class="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" [attr.d]="showRawData ? 'M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21' : 'M15 12a3 3 0 11-6 0 3 3 0 016 0z M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z'"/>
              </svg>
              {{ showRawData ? 'Ocultar' : 'Mostrar' }}
            </button>
          </div>
        </div>
      </div>
      <div *ngIf="showRawData" class="p-6">
        <pre class="p-4 bg-gray-900 text-gray-100 rounded-lg text-xs overflow-x-auto max-h-96 border border-gray-700">{{ rawWebhookDataFormatted }}</pre>
      </div>
    </div>

    <!-- Audit Info -->
    <div class="rounded-xl border border-stroke bg-white shadow-default dark:border-strokedark dark:bg-boxdark">
      <div class="border-b border-stroke px-6 py-4 dark:border-strokedark">
        <h3 class="font-semibold text-gray-900 dark:text-white flex items-center">
          <svg class="mr-2 h-5 w-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
          </svg>
          Auditoría
        </h3>
      </div>
      <div class="p-6 space-y-4">
        <div class="flex items-start">
          <span class="text-sm font-medium text-gray-500 dark:text-gray-400 w-32">Creado:</span>
          <div class="flex-1">
            <p class="text-sm text-gray-900 dark:text-white">{{ formatDate(request.createdAt) }}</p>
            <p *ngIf="request.createdBy" class="text-xs text-gray-500 dark:text-gray-400 mt-1">por {{ request.createdBy }}</p>
          </div>
        </div>
        <div class="flex items-start">
          <span class="text-sm font-medium text-gray-500 dark:text-gray-400 w-32">Actualizado:</span>
          <div class="flex-1">
            <p class="text-sm text-gray-900 dark:text-white">{{ formatDate(request.updatedAt) }}</p>
            <p *ngIf="request.updatedBy" class="text-xs text-gray-500 dark:text-gray-400 mt-1">por {{ request.updatedBy }}</p>
          </div>
        </div>
      </div>
    </div>
  </div>
</div>
